/**
 * 👷 Mitarbeiter — Anmeldung am Gerät (Name + PIN zu Schichtbeginn) und Stempel „wer hat was gemacht".
 *
 * - Die Mitarbeiter gehören zum KONTO (Server, Einstellungen → Mitarbeiter).
 * - Am GERÄT ist höchstens einer angemeldet; das Gerät merkt ihn sich bis „Schicht beenden"/Wechsel.
 * - Beim Erfassen/Ausgeben stempelt die App den Angemeldeten aufs Paket (erfasstVon/ausgegebenVon).
 *   Der Server übernimmt den Stempel nur beim echten Übergang und nur mit einer ID dieses Kontos.
 * - Ohne angelegte Mitarbeiter: keine Anmeldung, kein Stempel — die App verhält sich wie bisher.
 *
 * Nicht zu verwechseln mit dem Mitarbeiter-MODUS (staffMode.js): der sperrt die Oberfläche eines Geräts.
 */
import { api, getStoredUser } from './apiService';

const KEY_AKTUELL = 'kiosk_mitarbeiter';         // { uid, id, name, seit }
const KEY_LISTE = 'kiosk_mitarbeiter_liste';     // { uid, liste: [{id, name}] }
const KEY_SPAETER = 'kiosk_mitarbeiter_spaeter'; // sessionStorage: Anmeldung für diese Sitzung übersprungen

function kontoUid() {
  // Konto-Kennung des Geräts (Login speichert id/googleId) — schützt vor einem Mitarbeiter aus einem anderen Konto
  try { const u = getStoredUser() || {}; return String(u.id || u.googleId || u.uid || '') || null; } catch { return null; }
}
function lies(key) {
  try { return JSON.parse(localStorage.getItem(key) || 'null'); } catch { return null; }
}
function schreib(key, wert) {
  try {
    if (wert == null) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(wert));
  } catch { /* */ }
}
function melde() {
  try { window.dispatchEvent(new CustomEvent('kiosk:mitarbeiter')); } catch { /* */ }
}

// ── Liste (gecacht fürs schnelle Anzeigen, frisch vom Server bei ladeListe) ──

/** Zuletzt bekannte Mitarbeiter dieses Kontos (leer = Funktion aus). */
export function bekannteListe() {
  const c = lies(KEY_LISTE);
  return c && c.uid === kontoUid() && Array.isArray(c.liste) ? c.liste : [];
}

export function mitarbeiterAktiv() {
  return bekannteListe().length > 0;
}

export async function ladeListe() {
  const r = await api.get('/mitarbeiter');
  const liste = Array.isArray(r && r.mitarbeiter) ? r.mitarbeiter : [];
  const vorher = JSON.stringify(bekannteListe());
  // ohne Mitarbeiter nichts im Gerät ablegen (Konten ohne die Funktion bleiben exakt wie bisher)
  schreib(KEY_LISTE, liste.length ? { uid: kontoUid(), liste } : null);
  // Angemeldeter wurde entfernt → abmelden; umbenannt → Namen nachziehen
  const a = aktueller();
  if (a) {
    const x = liste.find((m) => m.id === a.id);
    if (!x) schreib(KEY_AKTUELL, null);
    else if (x.name !== a.name) schreib(KEY_AKTUELL, { ...lies(KEY_AKTUELL), name: x.name });
  }
  if (vorher !== JSON.stringify(liste) || (a && !aktueller())) melde();
  return liste;
}

// ── Verwaltung (Einstellungen) ──

export async function anlegen(name, pin) {
  const r = await api.post('/mitarbeiter', { name, pin });
  await ladeListe().catch(() => {});
  return r;
}
export async function aendern(id, patch) {
  const r = await api.put(`/mitarbeiter/${id}`, patch);
  await ladeListe().catch(() => {});
  return r;
}
export async function entfernen(id) {
  await api.delete(`/mitarbeiter/${id}`);
  await ladeListe().catch(() => {});
}

// ── Anmeldung am Gerät ──

/** Angemeldeter Mitarbeiter dieses Geräts ({id, name}) oder null. */
export function aktueller() {
  const a = lies(KEY_AKTUELL);
  if (!a || !a.id || a.uid !== kontoUid()) return null;
  return { id: a.id, name: a.name, seit: a.seit };
}

/** PIN prüfen (Server) und anmelden. Wirft mit .status 403 (PIN falsch) / 429 (gesperrt). */
export async function anmelden(id, pin) {
  const r = await api.post(`/mitarbeiter/${id}/anmelden`, { pin });
  schreib(KEY_AKTUELL, { uid: kontoUid(), id: r.id, name: r.name, seit: new Date().toISOString() });
  try { sessionStorage.removeItem(KEY_SPAETER); } catch { /* */ }
  melde();
  return r;
}

export function abmelden() {
  schreib(KEY_AKTUELL, null);
  melde();
}

/** Soll das Gerät nach dem Mitarbeiter fragen? (Funktion an, niemand angemeldet, nicht „später" gewählt) */
export function anmeldungNoetig() {
  if (!mitarbeiterAktiv() || aktueller()) return false;
  try { return sessionStorage.getItem(KEY_SPAETER) !== '1'; } catch { return true; }
}
export function spaeter() {
  try { sessionStorage.setItem(KEY_SPAETER, '1'); } catch { /* */ }
  melde();
}

/** ID für den Paket-Stempel — null, wenn niemand angemeldet ist. */
export function stempelId() {
  const a = aktueller();
  return a ? a.id : null;
}

/**
 * Setzt den Stempel `feld` (erfasstVon/ausgegebenVon) auf den Angemeldeten — oder entfernt einen alten,
 * wenn niemand angemeldet ist. Ohne Mitarbeiter bleibt das Paket-Objekt unverändert.
 */
export function stempele(pkg, feld) {
  const id = stempelId();
  if (id) return { ...pkg, [feld]: id };
  if (pkg && feld in pkg) { const neu = { ...pkg }; delete neu[feld]; return neu; }
  return pkg;
}

/** Name zu einer ID (für „Erfasst von …" in den Details); unbekannt → ''. */
export function nameVon(id) {
  if (!id) return '';
  const x = bekannteListe().find((m) => m.id === id);
  return x ? x.name : '';
}

// ── Auswertung (Statistik) ──
export function auswertung(tage) {
  return api.get('/statistics/mitarbeiter', { tage });
}
